/**
 * The one i18next instance, initialised the moment this module is imported.
 *
 * It is the engine under `t.ts`. Nothing outside this directory imports
 * `i18next` directly: a second instance, or a second `init`, would be a second
 * catalogue with its own idea of the language in force.
 *
 * **It initialises synchronously, because `t` has to.** 185 render bodies call
 * `t` in the middle of drawing a screen and cannot await anything, so
 * `initAsync: false` with English `resources` in hand means the catalogue is
 * ready on the first line that asks for a word. A downloaded language arrives
 * later through `addResourceBundle` and `changeLanguage`, which is a swap and
 * not a wait.
 *
 * **`keySeparator: false` is the load-bearing line.** The catalogue is one
 * flat map of dotted keys, and 52 of them are both a leaf and the prefix of
 * another key — `authority.owner` beside `authority.owner.help`,
 * `record.daily_sales.items` beside `record.daily_sales.items.dishes`. Under a
 * separator i18next would read the first segment as a branch and the leaf
 * would be unreachable. Six of those prefixes are `nameKey` values written
 * into database rows, so the keys cannot be renamed to suit a tree.
 * `nsSeparator: ':'` is kept, because no key contains a colon.
 *
 * **A missing English key throws, on every platform and in every build.** That
 * is `t.ts`'s rule and it lives here now, in `parseMissingKeyHandler`, which
 * i18next calls after resolution fails. It is not gated on `__DEV__`: a key
 * that resolves to nothing is a defect in the catalogue, and a screen quietly
 * painting the key name is how a word goes missing for a release. A key
 * missing from a DOWNLOADED language is a different thing and falls back to
 * English through `fallbackLng`, silently and on purpose.
 */
/**
 * **`intl-pluralrules` is imported here, on the line above `i18next`, and that
 * position is load-bearing.** Hermes has no `Intl.PluralRules` at all — not a
 * partial one, not an English-only one; `new Intl.PluralRules('hi')` throws
 * `undefined cannot be used as a constructor`. i18next has had no fallback
 * since v24, and `PluralResolver.getRule` builds its rule inside a `try`, so
 * with the polyfill absent it swallows that TypeError and returns no rule.
 * Every plural then resolves by nothing, and the six languages that put zero
 * in the singular — hi, hi-Latn, bn, gu, kn, pa — are wrong at count 0 and
 * right everywhere else.
 *
 * It used to sit in `apps/mobile/src/lib/polyfills.ts`, imported first thing
 * in `app/_layout.tsx`. **That was structurally too late and the device proved
 * it.** Expo Router eagerly evaluates every `_layout.tsx` while building its
 * route store, to read `unstable_settings`, and it does that inside
 * `ContextNavigator`'s render — before `app/_layout.tsx`'s own module body
 * runs. `app/(auth)/_layout.tsx` sorts first (`(` before `_`) and reaches
 * `header-back-button` → `@/lib/i18n` on its line 4. The app died on launch on
 * both platforms, every route failing to load, because Metro caches a module
 * that threw and rethrows it forever.
 *
 * So the polyfill belongs beside the instance that needs it rather than in an
 * app file that something can get in front of. Whoever imports i18next first
 * gets the polyfill first, and there is no import order left to get wrong.
 *
 * It is safe everywhere: the package installs itself only when
 * `Intl.PluralRules.prototype.selectRange` is missing, so on Bun and in a
 * browser it is a no-op.
 */
import 'intl-pluralrules';

/// <reference path="./i18next-augment.d.ts" />
import i18next from 'i18next';

import { AGENT_LANGUAGE_TAGS } from '../agent-language';
import { formatNumber } from '../format';
import { en } from './en';

i18next.init({
  initAsync: false,
  lng: 'en',
  fallbackLng: 'en',
  // The eleven the picker offers. A tag outside the list is not loadable, so
  // a typo in a stored preference falls back rather than asking the bucket
  // for a language that was never translated.
  supportedLngs: [...AGENT_LANGUAGE_TAGS],
  defaultNS: 'app',
  ns: ['app'],
  resources: { en: { app: en } },
  // **Without this, no language but English can ever reach a screen.**
  // `loadResources` asks the backend only when `!options.resources ||
  // options.partialBundledLanguages`, and English IS in `resources` above, so
  // i18next would decide it already holds everything it needs and never call
  // out. `changeLanguage('hi')` would then swap the tag, resolve every key
  // through `fallbackLng` and draw the same English screen with no error
  // anywhere. Measured against i18next 26.4.2, `dist/esm/i18next.js`.
  //
  // Nothing pays for it where there is no backend: `Connector.prepareLoading`
  // answers "No backend was added via i18next.use" — a debug-only warning,
  // silent outside `debug: true` — and calls back at once. That is `bun test`,
  // and it is `apps/server` until Phase 6.
  partialBundledLanguages: true,
  keySeparator: false,
  nsSeparator: ':',
  returnNull: false,
  interpolation: {
    // React Native has no innerHTML. With escaping on, an apostrophe renders
    // as `&#x27;` on a screen a shopkeeper reads.
    escapeValue: false,
    skipOnVariables: true,
  },
  parseMissingKeyHandler: (key: string): string => {
    throw new Error(`i18n key "${key}" resolves to nothing. Add it to packages/shared/src/i18n/en.ts.`);
  },
});

/**
 * `{{count, number}}` — the one formatter, and the reason a number in a value
 * is never written by hand.
 *
 * *Writing in the UI* rule 6: "Never write a number by hand." The catalogue
 * could not keep it while a placeholder was filled by the screen, and it
 * showed: `team.count_other` was filled with `String(count)` on the record's
 * access screen and with `formatNumber(count)` on the team tile, so the same
 * label drew `100000` on one and `1,00,000` on the other. Indian grouping is
 * not a preference in the launch market; it is how the number is read.
 *
 * So every numeric marker carries `, number` and lands here, and `formatNumber`
 * in `../format.ts` is the only thing that turns a count into digits.
 *
 * **It is registered AFTER `init`.** `services.formatter` does not exist until
 * i18next has initialised, and `init` is synchronous above, so these lines run
 * with the service in hand. The throw is not defensive noise: the service is
 * optional in i18next's own types, and a silent `?.` here would put every
 * grouped number back to `100000` with nothing to see it.
 */
const { formatter } = i18next.services;
if (formatter === undefined) {
  throw new Error('i18next has no formatter service, so `{{count, number}}` would draw an ungrouped number.');
}
formatter.add('number', (value: unknown): string => formatNumber(value as number));

export { i18next };
